import { useEffect, useState } from 'react';
import { useDepartmentChildrenLazyQuery } from 'graphql-common';

type Args = {
  departmentId?: string | null;
  skip?: boolean;
};

// Walks `departmentChildren` level by level (siblings fetched in parallel)
// to collect every id under `departmentId`, so a parent picker can exclude
// a department's whole subtree — not just itself — and pre-empt the BE's
// `cycle_detected` ("cannot be nested under its own descendant") error.
// Bounded by the BE's max nesting depth (5), so at most 4 more round trips.
// Also tracks `subtreeDepth` — how many extra levels hang below
// `departmentId` today — so a parent picker can shrink how deep a new
// parent is allowed to be: re-parenting doesn't just move `departmentId`
// itself, it drags its whole subtree along, so a candidate parent that
// looks shallow enough on its own can still push `departmentId`'s deepest
// descendant past the BE's max depth.
export default function useDepartmentDescendantIds({
  departmentId,
  skip,
}: Args): {
  descendantIds: Set<string>;
  subtreeDepth: number;
  loading: boolean;
} {
  const [fetchChildren] = useDepartmentChildrenLazyQuery({
    fetchPolicy: 'cache-and-network',
  });
  const [descendantIds, setDescendantIds] = useState<Set<string>>(new Set());
  const [subtreeDepth, setSubtreeDepth] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (skip || !departmentId) {
      setDescendantIds(new Set());
      setSubtreeDepth(0);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setDescendantIds(new Set());
    setSubtreeDepth(0);

    const walk = async (parentIds: string[], level: number) => {
      if (!parentIds.length || cancelled) return;
      const results = await Promise.all(
        parentIds.map((id) => fetchChildren({ variables: { id } })),
      );
      if (cancelled) return;

      const collectedIds: string[] = [];
      const nextParentIds: string[] = [];
      results.forEach(({ data }) => {
        (data?.data?.children || []).forEach((child) => {
          collectedIds.push(String(child.id));
          if (Number(child.childrenCount) > 0) {
            nextParentIds.push(String(child.id));
          }
        });
      });

      if (collectedIds.length) {
        setDescendantIds((previous) => new Set([...previous, ...collectedIds]));
        setSubtreeDepth((previous) => Math.max(previous, level));
      }
      await walk(nextParentIds, level + 1);
    };

    walk([departmentId], 1).finally(() => {
      if (!cancelled) setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [departmentId, skip, fetchChildren]);

  return { descendantIds, subtreeDepth, loading };
}
